import { useMemo, useState } from "react";
import {
  ClipboardList,
  Layers,
  Search as SearchIcon,
  Bookmark,
  CheckCircle2,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import { SearchBar } from "../components/SearchBar";
import { FlipCard } from "../components/FlipCard";
import { useBookmarks } from "../hooks/useBookmarks";
import { cardsData } from "../content";
import { searchCards } from "../utils/search";

function getSavedCardIds(
  cards: Array<{ id: string }>,
  isBookmarked: (id: string, type: "card") => boolean
) {
  return cards.filter((card) => isBookmarked(card.id, "card"));
}

function SectionTitle({
  icon: Icon,
  eyebrow,
  title,
  description,
}: {
  icon: React.ComponentType<{ className?: string }>;
  eyebrow: string;
  title: string;
  description: string;
}) {
  return (
    <div className="mb-6">
      <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-cyan-300">
        <Icon className="h-4 w-4" />
        {eyebrow}
      </div>

      <h2 className="text-2xl font-extrabold tracking-tight text-white md:text-3xl">
        {title}
      </h2>

      <p className="mt-3 max-w-3xl text-sm leading-6 text-white/70 md:text-base">
        {description}
      </p>
    </div>
  );
}

export function Cards() {
  const [searchQuery, setSearchQuery] = useState("");
  const [showSavedOnly, setShowSavedOnly] = useState(false);
  const { toggleBookmark, isBookmarked } = useBookmarks();

  const filteredCards = useMemo(() => {
    let results = !searchQuery.trim()
      ? cardsData.cards
      : searchCards(cardsData.cards, searchQuery);

    if (showSavedOnly) {
      results = results.filter((card) => isBookmarked(card.id, "card"));
    }

    return results;
  }, [searchQuery, showSavedOnly, isBookmarked]);

  const savedCards = useMemo(
    () => getSavedCardIds(cardsData.cards, isBookmarked),
    [isBookmarked]
  );

  const hasFilters = !!searchQuery.trim() || showSavedOnly;

  return (
    <div className="min-h-screen bg-[#070b14] text-white">
      <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-12">
        {/* HERO */}
        <section className="relative overflow-hidden rounded-[28px] border border-white/10 bg-gradient-to-br from-[#091224] via-[#0b1530] to-[#080d18] p-6 md:p-10">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(34,211,238,0.16),transparent_32%),radial-gradient(circle_at_bottom_left,rgba(217,70,239,0.12),transparent_30%)]" />

          <div className="relative grid gap-8 lg:grid-cols-[1.15fr_0.85fr] lg:items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-fuchsia-400/20 bg-fuchsia-500/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.28em] text-fuchsia-200">
                <ClipboardList className="h-4 w-4" />
                Fast Troubleshooting Reference
              </div>

              <h1 className="mt-5 text-4xl font-black tracking-tight md:text-5xl">
                Diagnostic Cards
              </h1>

              <p className="mt-4 max-w-2xl text-sm leading-7 text-white/72 md:text-base">
                Quick-flip troubleshooting cards built for fast diagnosis. Tap
                any card to reveal the likely cause and the fastest next fix.
              </p>

              <div className="mt-6 flex flex-wrap gap-3">
                <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white/85">
                  <span className="font-bold text-white">
                    {cardsData.cards.length}
                  </span>{" "}
                  total cards
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white/85">
                  Tap to flip
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white/85">
                  Save your favorites
                </div>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {[
                {
                  title: "Need a quick reference?",
                  description:
                    "Use cards when you want a fast cause-and-fix path without digging through longer content.",
                },
                {
                  title: "Need a tighter search?",
                  description:
                    "Search by problem name, symptom, or keyword to narrow the card set quickly.",
                },
                {
                  title: "Need your go-to fixes?",
                  description:
                    "Save frequently used cards so repeat issues are easier to handle later.",
                },
                {
                  title: "Need deeper learning?",
                  description:
                    "Use cards for fast help, then jump into Issues or Diagnosis for more detail.",
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="rounded-[24px] border border-white/10 bg-white/[0.05] p-5"
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 rounded-xl border border-emerald-300/20 bg-emerald-400/10 p-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-300" />
                    </div>

                    <div>
                      <h3 className="text-sm font-bold text-white">
                        {item.title}
                      </h3>
                      <p className="mt-2 text-sm leading-6 text-white/70">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SEARCH */}
        <section className="mt-8 rounded-[26px] border border-white/10 bg-white/[0.04] p-5 md:p-6">
          <SectionTitle
            icon={SearchIcon}
            eyebrow="Search"
            title="Find a diagnostic card fast"
            description="Search by problem name, likely symptom, or keyword to jump into the most useful quick-reference cards."
          />

          <div className="max-w-3xl">
            <SearchBar
              placeholder="Search diagnostic cards..."
              onSearch={setSearchQuery}
            />
          </div>
        </section>

        {/* STATUS / TOGGLES */}
        <section className="mt-6 rounded-[24px] border border-white/10 bg-gradient-to-br from-[#091224] via-[#0b1530] to-[#080d18] p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-400/10 px-4 py-2 text-cyan-300">
                <Layers className="h-4 w-4" />
                Showing {filteredCards.length} of {cardsData.cards.length}
              </div>

              <div className="inline-flex items-center gap-2 rounded-full border border-fuchsia-400/20 bg-fuchsia-500/10 px-4 py-2 text-fuchsia-200">
                <Bookmark className="h-4 w-4" />
                {savedCards.length} saved
              </div>

              {searchQuery.trim() ? (
                <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-white/70">
                  Search: {searchQuery}
                </div>
              ) : null}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setShowSavedOnly(false)}
                className={`inline-flex items-center justify-center rounded-2xl border px-5 py-3 text-sm font-semibold transition ${
                  !showSavedOnly
                    ? "border-cyan-300/35 bg-cyan-400/20 text-cyan-100"
                    : "border-white/10 bg-white/5 text-white/80 hover:border-cyan-300/20 hover:bg-white/10"
                }`}
              >
                All Cards
              </button>

              <button
                onClick={() => setShowSavedOnly(true)}
                className={`inline-flex items-center justify-center rounded-2xl border px-5 py-3 text-sm font-semibold transition ${
                  showSavedOnly
                    ? "border-cyan-300/35 bg-cyan-400/20 text-cyan-100"
                    : "border-white/10 bg-white/5 text-white/80 hover:border-cyan-300/20 hover:bg-white/10"
                }`}
              >
                Saved Only
              </button>
            </div>
          </div>
        </section>

        {/* EMPTY STATE */}
        {filteredCards.length === 0 ? (
          <section className="mt-8 rounded-[26px] border border-white/10 bg-white/[0.04] p-10 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl border border-cyan-300/15 bg-cyan-400/10">
              <SearchIcon className="h-8 w-8 text-cyan-300" />
            </div>

            <h2 className="mt-5 text-2xl font-extrabold tracking-tight text-white">
              No cards found
            </h2>

            <p className="mt-3 text-sm leading-6 text-white/68 md:text-base">
              Nothing matched your search or current saved filter.
            </p>

            <button
              onClick={() => {
                setSearchQuery("");
                setShowSavedOnly(false);
              }}
              className="mt-6 inline-flex items-center justify-center gap-2 rounded-2xl border border-cyan-300/35 bg-cyan-400/20 px-6 py-3 text-sm font-semibold text-cyan-100 transition hover:-translate-y-0.5 hover:border-cyan-200/60 hover:bg-cyan-400/30"
            >
              Clear Filters
              <ArrowRight className="h-4 w-4" />
            </button>
          </section>
        ) : (
          <section className="mt-8">
            <SectionTitle
              icon={Sparkles}
              eyebrow="Library"
              title="Flip through the card library"
              description={
                hasFilters
                  ? "Filtered results are shown below."
                  : "Browse the full quick-reference card collection below."
              }
            />

            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filteredCards.map((card, index) => (
                <div
                  key={card.id}
                  className="rounded-[24px] transition duration-300 hover:-translate-y-1"
                >
                  <FlipCard
                    card={card}
                    cardNumber={index + 1}
                    isBookmarked={isBookmarked(card.id, "card")}
                    onToggleBookmark={() =>
                      toggleBookmark({
                        id: card.id,
                        type: "card",
                        title: card.problem,
                      })
                    }
                  />
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}